import AttachFileIcon from "@mui/icons-material/AttachFileOutlined";
import SendIcon from "@mui/icons-material/SendRounded";
import { Chip, CircularProgress, IconButton, Paper, Stack, TextField, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import FileIcon from "@entrilia/shared/components/FileIcon";
import UserAvatar from "@entrilia/shared/components/UserAvatar";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { convertISODateTimeWithSeconds } from "@entrilia/shared/utilities/dateUtils";
import { getFileExtension } from "@entrilia/shared/utilities/fileHelper";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid";
import { wait } from "@entrilia/shared/utilities/promiseHelper";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../../api/adminApi";
import type { FileInfo } from "../../../../../api/types/fileTypes";
import type { WorkflowActivity, WorkflowSupervisorConversationTurn } from "../../../../../api/types/workflowTypes";
import WorkflowAgentLabel from "../../workflow-templates/WorkflowAgentLabel";
import UploadWorkflowAttachmentDialog from "./UploadWorkflowAttachmentDialog";
import { getActivityDisplayStatus } from "./workflowDetailsHelpers";

const submitResponse = withErrorHandling(adminApi.submitWorkflowSupervisorResponse);

const maxResponseWaitTimeMs = 20_000;

interface Props {
  workflowId: string;
  activity: WorkflowActivity;
  isUserAllowedToReply: boolean;
  onResponseSubmitted: () => void;
}

const findTurnIndexBySubmissionId = (turns: WorkflowSupervisorConversationTurn[], submissionId: string): number => {
  for (let i = turns.length - 1; i >= 0; i--) {
    if (turns[i]?.submissionId === submissionId) {
      return i;
    }
  }

  return -1;
};

const AttachmentChip = ({ catalogueId }: { catalogueId: string }) => {
  const getFileInfo = useCallback(() => adminApi.getFileDownloadInfo(catalogueId), [catalogueId]);
  const [fileInfo] = useFetch(getFileInfo);

  const fileName = fileInfo?.fileName ?? "Attachment";

  return (
    <Chip
      label={fileName}
      variant="outlined"
      icon={fileInfo ? <FileIcon fileExtension={getFileExtension(fileName)} /> : undefined}
    />
  );
};

const MessageBubble = ({ turn }: { turn: WorkflowSupervisorConversationTurn }) => {
  const userDisplayName = turn.userName || "User";

  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: "flex-start" }}>
      {turn.role === "Supervisor" ? (
        <WorkflowAgentLabel avatarOnly agentType="supervisor" />
      ) : (
        <UserAvatar userName={userDisplayName} size="small" />
      )}
      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "baseline" }}>
          <Typography variant="subtitle2">{turn.role === "Supervisor" ? "Agent" : userDisplayName}</Typography>
          <Typography variant="caption" color="textSecondary">
            {convertISODateTimeWithSeconds(turn.timestampUtc)}
          </Typography>
        </Stack>
        <Typography variant="body1">{turn.messageText}</Typography>
        {turn.attachmentDataCatalogIds && turn.attachmentDataCatalogIds.length > 0 && (
          <Stack direction="row" sx={{ gap: 0.5, flexWrap: "wrap", pt: 0.5 }}>
            {turn.attachmentDataCatalogIds.map((id) => (
              <AttachmentChip key={id} catalogueId={id} />
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

const PendingQuestionBubble = ({ question }: { question: string }) => (
  <Stack direction="row" spacing={1.5} sx={{ alignItems: "flex-start" }}>
    <WorkflowAgentLabel avatarOnly agentType="supervisor" />
    <Stack spacing={0.5}>
      <Typography variant="subtitle2">Agent</Typography>
      <Typography variant="body1">{question}</Typography>
    </Stack>
  </Stack>
);

const WorkflowSupervisorConversationSection = ({
  workflowId,
  activity,
  isUserAllowedToReply,
  onResponseSubmitted,
}: Props) => {
  const { sendNotificationError } = useNotificationContext();

  const [messageText, setMessageText] = useState("");
  const [attachments, setAttachments] = useState<FileInfo[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [showAttachDialog, setShowAttachDialog] = useState(false);
  const [pendingSubmissionId, setPendingSubmissionId] = useState<string>();
  const [pendingRequestRequestedAtSubmit, setPendingRequestRequestedAtSubmit] = useState<string>();

  if (!activity.workflowActivityId || (!activity.supervisorPendingRequest && !activity.supervisorConversation)) {
    return null;
  }

  const conversationTurns = activity.supervisorConversation?.history.items ?? [];

  const submittedTurnIndex =
    pendingSubmissionId === undefined ? -1 : findTurnIndexBySubmissionId(conversationTurns, pendingSubmissionId);

  const hasSupervisorReplyAfterSubmittedTurn =
    submittedTurnIndex !== -1 &&
    conversationTurns.slice(submittedTurnIndex + 1).some((turn) => turn.role === "Supervisor");

  const pendingRequest = activity.supervisorPendingRequest ?? { isActive: false, requestedKeys: [] };

  const hasUpdatedPendingRequest =
    pendingSubmissionId !== undefined &&
    activity.supervisorConversation?.lastSubmissionId === pendingSubmissionId &&
    pendingRequest.isActive &&
    pendingRequest.requestedAtUtc !== undefined &&
    pendingRequest.requestedAtUtc !== pendingRequestRequestedAtSubmit;

  const activityDisplayStatus = getActivityDisplayStatus(activity);

  const canReply =
    isUserAllowedToReply &&
    activityDisplayStatus === "PendingApproval" &&
    (activity.supervisorPendingRequest?.isActive === true || activity.supervisorConversation !== undefined);

  const awaitingResponse =
    pendingSubmissionId !== undefined && canReply && !hasSupervisorReplyAfterSubmittedTurn && !hasUpdatedPendingRequest;

  const showPendingQuestionOnly = conversationTurns.length === 0 && !!pendingRequest.question;

  const isSubmitDisabled = submitting || awaitingResponse || messageText.trim() === "";

  const handleSubmit = async () => {
    const text = messageText.trim();
    if (!text) {
      return;
    }

    const submissionId = generateGuid();

    setSubmitting(true);
    const [result, error] = await submitResponse(workflowId, defined(activity.workflowActivityId), {
      messageText: text,
      submissionId,
      attachmentDataCatalogIds: attachments.length > 0 ? attachments.map((a) => a.catalogueId) : undefined,
    });
    setSubmitting(false);

    if (error) {
      logError(error, "[WorkflowSupervisorConversationSection] submitResponse");
      sendNotificationError(error.message);
      return;
    }

    setMessageText("");
    setAttachments([]);

    if (result.outcome === "Duplicate") {
      onResponseSubmitted();
      return;
    }

    setPendingRequestRequestedAtSubmit(pendingRequest.requestedAtUtc);
    setPendingSubmissionId(submissionId);
    onResponseSubmitted();

    await wait(maxResponseWaitTimeMs);
    setPendingSubmissionId((current) => (current === submissionId ? undefined : current));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" && !event.shiftKey && !isSubmitDisabled) {
      event.preventDefault();
      void handleSubmit();
    }
  };

  const handleRemoveAttachment = (catalogueId: string) => {
    setAttachments((prev) => prev.filter((a) => a.catalogueId !== catalogueId));
  };

  return (
    <>
      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Stack spacing={2} sx={{ px: 2, py: 1.5 }}>
          {showPendingQuestionOnly && pendingRequest.question && (
            <PendingQuestionBubble question={pendingRequest.question} />
          )}

          {conversationTurns.map((turn) => (
            <MessageBubble key={turn.id} turn={turn} />
          ))}

          {canReply && awaitingResponse && (
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", py: 1 }}>
              <CircularProgress color="secondary" size={16} />
              <Typography variant="caption" color="textSecondary">
                Processing response...
              </Typography>
            </Stack>
          )}

          {canReply && !awaitingResponse && (
            <Paper variant="outlined">
              <Stack spacing={1} sx={{ p: 1.5 }}>
                {attachments.length > 0 && (
                  <Stack direction="row" sx={{ gap: 0.5, flexWrap: "wrap" }}>
                    {attachments.map((att) => (
                      <Chip
                        key={att.catalogueId}
                        label={att.fileName}
                        variant="outlined"
                        icon={<FileIcon fileExtension={getFileExtension(att.fileName)} />}
                        onDelete={() => handleRemoveAttachment(att.catalogueId)}
                      />
                    ))}
                  </Stack>
                )}

                <TextField
                  placeholder="Reply..."
                  multiline
                  maxRows={4}
                  fullWidth
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={submitting}
                  variant="standard"
                  slotProps={{ input: { disableUnderline: true } }}
                  sx={{ "& .MuiInputBase-input": { py: 0 } }}
                />

                <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
                  <IconButton color="secondary" onClick={() => setShowAttachDialog(true)} disabled={submitting}>
                    <AttachFileIcon />
                  </IconButton>
                  <IconButton color="primary" disabled={isSubmitDisabled} onClick={handleSubmit}>
                    <SendIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Paper>
      {showAttachDialog && (
        <UploadWorkflowAttachmentDialog
          workflowId={workflowId}
          onboard={false}
          existingFileNames={attachments.map((attachment) => attachment.fileName)}
          onClose={() => setShowAttachDialog(false)}
          onUploaded={(file) => setAttachments((prev) => [...prev, file.fileInfo])}
        />
      )}
    </>
  );
};

export default WorkflowSupervisorConversationSection;
